import L from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useRef } from "react";
import { IMapLocation } from "../../interfaces/map-location";
import styles from "./leaflet-map-details.module.scss";

interface IProps {
  locations: IMapLocation[];
  onLocationSelect?: (location: IMapLocation) => void;
  onLocationClick?: (location: IMapLocation) => void;
  selectedSite?: {
    id?: string;
    latitude: number | string;
    longitude: number | string;
    address?: string;
    value?: number;
  };
  height?: number;
  zoomControl?: boolean;
  hidePopup?: boolean;
  sidebarOffset?: number;
}

const initialCenter: [number, number] = [21.9162, 95.956];

const LeafletMapDetails: React.FC<IProps> = ({
  locations,
  onLocationSelect,
  onLocationClick,
  selectedSite,
  height = 600,
  zoomControl = false,
  hidePopup = false,
  sidebarOffset = 0,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<{ location: IMapLocation; marker: L.Marker }[]>([]);

  // Initialize Map
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: initialCenter,
      zoom: 6,
      zoomControl: zoomControl,
    });

    // Google Maps Tile Layer
    L.tileLayer("https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", {
      maxZoom: 20,
      attribution:
        '&copy; <a href="https://www.google.com/maps">Google Maps</a> | &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [zoomControl]);

  // Update Markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear existing markers
    markersRef.current.forEach((item) => item.marker.remove());
    markersRef.current = [];

    if (!locations || locations.length === 0) return;

    const bounds = L.latLngBounds([]);

    locations.forEach((location) => {
      const lat = Number(location.lat);
      const lng = Number(location.lng);

      if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;

      bounds.extend([lat, lng]);

      const pinColor = location.statusColor || "#0ea11c";
      const bgColor = location.statusBgColor || "#ecfaed";
      const statusLabel = location.statusLabel || "Active";
      const capacityVal =
        location.value !== undefined ? `${location.value} kW` : "N/A";
      const addressText = location.address || "Myanmar";

      // SVG Drop Pin Icon
      const svgIcon = `
        <div class="${styles.customPinWrapper}">
          <svg width="34" height="42" viewBox="0 0 34 42" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 0C7.61116 0 0 7.61116 0 17C0 27.2 14.875 40.375 16.32 41.6075C16.7156 41.9472 17.2844 41.9472 17.68 41.6075C19.125 40.375 34 27.2 34 17C34 7.61116 26.3888 0 17 0Z" fill="${pinColor}"/>
            <circle cx="17" cy="16" r="10" fill="#FFFFFF"/>
            <path d="M16 9.5V14.5H12.5L18 22.5V17.5H21.5L16 9.5Z" fill="${pinColor}"/>
          </svg>
        </div>
      `;

      const customIcon = L.divIcon({
        html: svgIcon,
        className: "",
        iconSize: [34, 42],
        iconAnchor: [17, 42],
        popupAnchor: [0, -40],
      });

      const marker = L.marker([lat, lng], {
        icon: customIcon,
      }).addTo(map);

      if (!hidePopup) {
        // Popup Content Card
        const popupHtml = `
          <div class="${styles.customPopupCard}">
            <div class="${styles.popupTitle}">${location.name}</div>
            <div class="${styles.popupSubTitle}">${location.serviceProvider || "SOLAR PLANT"}</div>
            <div class="${styles.popupRow}">
              <span class="${styles.popupLabel}">Capacity:</span>
              <span class="${styles.popupValue}">${capacityVal}</span>
            </div>
            <div class="${styles.popupRow}">
              <span class="${styles.popupLabel}">Location:</span>
              <span class="${styles.popupValue}">${addressText}</span>
            </div>
            <div class="${styles.popupBadgeRow}">
              <span class="${styles.popupLabel}">Workflow Stage:</span>
              <span class="${styles.statusBadge}" style="background-color: ${bgColor}; color: ${pinColor}; border: 1px solid ${pinColor}33;">
                ${statusLabel}
              </span>
            </div>
          </div>
        `;

        marker.bindPopup(popupHtml, {
          closeButton: true,
          autoPan: true,
        });
      }

      // Hover Event -> Open Popup
      marker.on("mouseover", () => {
        if (!hidePopup) {
          marker.openPopup();
        }
      });

      // Click Event -> Navigate / Select Site
      marker.on("click", () => {
        if (onLocationClick) {
          onLocationClick(location);
        } else if (onLocationSelect) {
          onLocationSelect(location);
        }
      });

      markersRef.current.push({ location, marker });
    });

    const hasValidSelected =
      selectedSite &&
      !isNaN(Number(selectedSite.latitude)) &&
      !isNaN(Number(selectedSite.longitude)) &&
      (Number(selectedSite.latitude) !== 0 ||
        Number(selectedSite.longitude) !== 0);

    if (bounds.isValid() && !hasValidSelected) {
      const isDesktop = window.innerWidth > 991;
      const leftPad = sidebarOffset && isDesktop ? sidebarOffset + 40 : 50;
      map.fitBounds(bounds, {
        paddingTopLeft: [leftPad, 50],
        paddingBottomRight: [50, 50],
        maxZoom: 14,
      });
    }
  }, [
    locations,
    onLocationSelect,
    onLocationClick,
    selectedSite,
    hidePopup,
    sidebarOffset,
  ]);

  // Center & Focus on Selected Site (Offset for left sidebar)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !selectedSite) return;

    const lat = Number(selectedSite.latitude);
    const lng = Number(selectedSite.longitude);

    if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;

    map.setView([lat, lng], 14, { animate: false });

    const isDesktop = window.innerWidth > 991;
    if (sidebarOffset && isDesktop) {
      map.panBy([-Math.round(sidebarOffset / 2), 0], { animate: false });
    }

    const matched = markersRef.current.find(
      (item) =>
        (selectedSite.id && item.location.id === selectedSite.id) ||
        (Math.abs(Number(item.location.lat) - lat) < 0.0001 &&
          Math.abs(Number(item.location.lng) - lng) < 0.0001),
    );

    if (matched && !hidePopup) {
      matched.marker.openPopup();
    }
  }, [selectedSite, hidePopup, sidebarOffset]);

  return (
    <div
      ref={mapContainerRef}
      className={styles.mapContainer}
      style={{ height: `${height}px` }}
    />
  );
};

export default LeafletMapDetails;
